<script setup lang="ts">
import { ParashahType } from 'types/types'
import { computed, onMounted, reactive, ref, watchEffect } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import { formattedParashah } from '@/composables/format'
import { setupAutoSingleSelect } from '@/composables/useAutoSingleSelect'
import { queryParshiyot } from '@/store/queryHelpers'

interface Props {
  clearable?: boolean
  disabled?: boolean
  externalSet?: boolean
  only?: Array<ParashahType> | null
  parashah: ParashahType | null
  preventAutoSingleSelect?: boolean
  remove?: Array<ParashahType>
  resetSelectionOnMultiple?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  clearable: true,
  disabled: false,
  externalSet: false,
  only: null,
  preventAutoSingleSelect: false,
  remove: (): Array<ParashahType> => [],
  resetSelectionOnMultiple: false,
})

const logger = useLogger()

const emit = defineEmits(['update:parashah'])
const innerParashahSelect = ref<HTMLElement | null>(null)

const focus = (): void => {
  innerParashahSelect.value?.focus()
}

const parshiyotResult = ref<Array<ParashahType>>([])

const filteredParshiyot = computed<Array<ParashahType>>(() => {
  const parshiyot = props.only ?? parshiyotResult.value
  if (!parshiyot?.length) {
    return []
  }

  return parshiyot.filter((a) => !props.remove.find((b) => a.reading.id === b.reading.id)).toSorted((a, b) => a.order - b.order)
})

const state = reactive({
  parshiyot: [] as Array<ParashahType>,
  selectedParashah: props.parashah as ParashahType | null,
})

onMounted(() => {
  queryParshiyot().then((parshiyot) => {
    parshiyotResult.value = parshiyot
  })

  watchEffect(() => {
    if (props.externalSet) {
      state.selectedParashah = props.parashah
    }
  })

  setupAutoSingleSelect({
    filteredItems: filteredParshiyot,
    preventAutoSelect: () => props.preventAutoSingleSelect,
    resetOnMultiple: () => props.resetSelectionOnMultiple,
    setSelected: (item) => {
      state.selectedParashah = item
    },
  })

  watchEffect(() => {
    logger.debug('ParashahSelect watch state.selectedParashah, state.selectedParashah =')
    logger.debug(state.selectedParashah)
    emit('update:parashah', state.selectedParashah)
  })
})
defineExpose({ focus })
</script>

<template>
  <v-autocomplete
    ref="innerParashahSelect"
    v-model="state.selectedParashah"
    auto-select-first
    :clearable="props.clearable"
    :disabled="props.disabled"
    :item-title="
      (parashah: ParashahType) => {
        return formattedParashah(parashah)
      }
    "
    :items="filteredParshiyot"
    :label="state.selectedParashah ? 'Parashah' : 'Choose a parashah'"
    :loading="!filteredParshiyot || !filteredParshiyot.length"
    return-object
    variant="underlined"
  />
</template>
